import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtCoins } from "@/components/shop/PurchaseModal";
import {
  Radio, Play, BookOpen, CalendarDays, FolderOpen, Lock, ArrowRight,
  Download, ShieldCheck, Users, Zap, Heart, Clock, GraduationCap, MapPin, Phone, Search, ChevronRight,
} from "lucide-react";


const imgSrc = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${u.startsWith("/") ? "" : "/"}${u}`;
};

const ProductImg = ({ url, alt, className, icon: Icon }) => {
  const src = imgSrc(url);
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-white/[0.03] border border-white/5 ${className}`}>
        <Icon className="h-8 w-8 text-[#334155]" />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" className={`object-cover bg-white/[0.03] border border-white/5 ${className}`} />;
};

const PricePill = ({ item, testid }) => (
  item.access_type === "coin" && !item.entitled ? (
    <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5" data-testid={testid}>{fmtCoins(item.coin_price_minor)}</span>
  ) : (
    <span className="text-[10px] font-mono tracking-wider uppercase text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 rounded-full px-2.5 py-0.5" data-testid={testid}>{item.purchased ? "OWNED" : "FREE"}</span>
  )
);

const ListRow = ({ img, icon: Icon, title, desc, badge, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${active ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : "border-white/5 bg-white/[0.02] hover:border-white/15"}`}>
    <ProductImg url={img} alt={title} icon={Icon} className="h-16 w-14 rounded-lg shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-white truncate">{title}</p>
      {desc && <p className="text-xs text-[#64748B] line-clamp-2 mt-0.5">{desc}</p>}
      {badge && <div className="mt-1.5">{badge}</div>}
    </div>
    <ChevronRight className={`h-4 w-4 shrink-0 ${active ? "text-[#D4AF37]" : "text-[#334155]"}`} />
  </button>
);

const MetaItem = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-2.5">
    <div className="h-9 w-9 rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/5 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] font-mono tracking-widest uppercase text-[#64748B]">{label}</p>
      <p className="text-xs text-white truncate" data-testid={testid}>{value}</p>
    </div>
  </div>
);

const DescriptionBlock = ({ content }) => (
  content ? (
    <div className="mt-7 pt-6 border-t border-white/5">
      <p className="font-heading text-base font-semibold text-white mb-2">Description</p>
      <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-line">{content}</p>
    </div>
  ) : null
);

const LibraryShell = ({ icon: Icon, title, tagline, tabs, searchPH, query, setQuery, list, detail, testid }) => (
  <div className="grid lg:grid-cols-[320px,1fr] gap-5 items-start" data-testid={testid}>
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 space-y-4 lg:sticky lg:top-24">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[#D4AF37]" />
        </div>
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold text-white tracking-wide">{title}</p>
          <p className="text-xs text-[#64748B] mt-0.5">{tagline}</p>
        </div>
      </div>
      {tabs}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#475569]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPH}
          className="w-full rounded-lg border border-white/10 bg-white/[0.03] pl-9 pr-3 py-2 text-xs text-white placeholder:text-[#475569] focus:border-[#D4AF37]/50 focus:outline-none"
          data-testid={`${testid}-search`} />
      </div>
      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">{list}</div>
    </div>
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 sm:p-7 min-w-0" data-testid={`${testid}-detail`}>
      {detail}
    </div>
  </div>
);

const EmptyLibrary = ({ icon: Icon, text, testid }) => (
  <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-12 flex flex-col items-center text-center" data-testid={testid}>
    <Icon className="h-10 w-10 text-[#334155] mb-4" />
    <p className="text-sm text-[#64748B]">{text}</p>
  </div>
);

import { fmtMYT, fmtMYTDate } from "@/lib/myt";
const fmtDT = (v) => fmtMYT(v);

const relTime = (v) => {
  if (!v) return "";
  const s = (Date.now() - new Date(v).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} mins ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hour${Math.floor(s / 3600) === 1 ? "" : "s"} ago`;
  return fmtMYTDate(v);
};

function CardHead({ icon: Icon, label, accent, badge }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2.5">
        <div className={`h-9 w-9 rounded-lg border flex items-center justify-center ${accent.border} ${accent.bg}`}>
          <Icon className={`h-4 w-4 ${accent.text}`} />
        </div>
        <span className={`text-xs font-mono tracking-[0.2em] font-semibold ${accent.text}`}>{label}</span>
      </div>
      {badge}
    </div>
  );
}

export default function MemberLiveLearn() {
  const [signals, setSignals] = useState(null);
  const [live, setLive] = useState(null);
  const [videos, setVideos] = useState(null);
  const [ebooks, setEbooks] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Standalone Topics: Live & Learn no longer owns the broadcast. Preserve backward
  // compatibility — old notification deep-links (?topic=<id>&msg=<id>) and the old
  // ?view=topics entry redirect to the standalone /member/topics page. Query keys are
  // unchanged, so existing service-worker click routing keeps working untouched.
  useEffect(() => {
    const topic = searchParams.get("topic");
    if (topic) {
      const qs = new URLSearchParams({ topic });
      const msg = searchParams.get("msg");
      if (msg) qs.set("msg", msg);
      navigate(`/member/topics?${qs.toString()}`, { replace: true });
      return;
    }
    if (searchParams.get("view") === "topics") {
      navigate("/member/topics", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [webinars, setWebinars] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [resInd, setResInd] = useState(null);
  const [resEa, setResEa] = useState(null);
  const [resTab, setResTab] = useState("indicator");
  const [activeModule, setActiveModule] = useState(null); // null = module launcher; otherwise inside one module
  const [sel, setSel] = useState({}); // selected product id per module
  const [query, setQuery] = useState(""); // library search (reset on module switch)

  useEffect(() => {
    api.get("/member/activity/signals").then((r) => setSignals(r.data)).catch(() => setSignals({ ib_name: null, active: [], history: [], disclosure: "" }));
    api.get("/member/activity/live").then((r) => setLive(r.data)).catch(() => setLive({ ib_name: null, sessions: [] }));
    api.get("/member/activity/videos").then((r) => setVideos(r.data)).catch(() => setVideos({ ib_name: null, plan: null, items: [] }));
    api.get("/member/activity/ebooks").then((r) => setEbooks(r.data)).catch(() => setEbooks({ ib_name: null, plan: null, items: [] }));
    api.get("/member/activity/webinars").then((r) => setWebinars(r.data)).catch(() => setWebinars({ ib_name: null, items: [] }));
    api.get("/member/activity/coaching").then((r) => setCoaching(r.data)).catch(() => setCoaching({ ib_name: null, items: [] }));
    api.get("/member/activity/resources/indicator").then((r) => setResInd(r.data)).catch(() => setResInd({ ib_name: null, plan: null, items: [] }));
    api.get("/member/activity/resources/ea").then((r) => setResEa(r.data)).catch(() => setResEa({ ib_name: null, plan: null, items: [] }));
  }, []);

  const [unlocking, setUnlocking] = useState("");
  const reloadGated = () => {
    api.get("/member/activity/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/member/activity/ebooks").then((r) => setEbooks(r.data)).catch(() => {});
    api.get("/member/activity/resources/indicator").then((r) => setResInd(r.data)).catch(() => {});
    api.get("/member/activity/resources/ea").then((r) => setResEa(r.data)).catch(() => {});
  };
  const NO_REFUND = "NO REFUND: All COINS payments for bookings are final and non-refundable.";
  const reloadBookings = () => {
    api.get("/member/activity/webinars").then((r) => setWebinars(r.data)).catch(() => {});
    api.get("/member/activity/coaching").then((r) => setCoaching(r.data)).catch(() => {});
  };
  const bookItem = async (item, kind) => {
    const isPaid = item.access_type === "coin" && item.coin_price_minor > 0;
    const msg = isPaid
      ? `Book "${item.title}" for ${fmtCoins(item.coin_price_minor)}?\n\n${NO_REFUND}`
      : `Request a free seat for "${item.title}"?\n\nYour booking will be pending IB approval.`;
    if (!window.confirm(msg)) return;
    setBooking(item.id);
    try {
      const res = await api.post(`/member/activity/${kind}/${item.id}/book`, {});
      if (res.data?.booking?.status === "pending") toast.success("Booking submitted — pending IB approval");
      else toast.success("Booked — your seat is confirmed");
      reloadBookings();
      window.dispatchEvent(new Event("pm-coin-balance-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally {
      setBooking("");
    }
  };

  // Personal Coaching: the client shares THEIR phone number so the owning IB can contact them
  const [phoneTarget, setPhoneTarget] = useState(null);
  const [phoneValue, setPhoneValue] = useState("");
  const confirmCoaching = async () => {
    if (phoneValue.replace(/[^\d]/g, "").length < 7) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBooking(phoneTarget.id);
    try {
      await api.post(`/member/activity/coaching/${phoneTarget.id}/book`, { client_phone: phoneValue.trim() });
      toast.success("Booked — your phone number was shared with your IB");
      setPhoneTarget(null);
      setPhoneValue("");
      reloadBookings();
      window.dispatchEvent(new Event("pm-coin-balance-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally {
      setBooking("");
    }
  };
  const unlock = async (item) => {
    if (!window.confirm(`Unlock "${item.title}" for ${fmtCoins(item.coin_price_minor)}?`)) return;
    setUnlocking(item.id);
    try {
      await api.post(`/member/activity/${item.id}/purchase`, {});
      toast.success("Unlocked — access granted");
      reloadGated();
      window.dispatchEvent(new Event("pm-coin-balance-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    } finally {
      setUnlocking("");
    }
  };

  // Indicator resources (and ONLY indicators) require a TradingView Username before claim/purchase
  const [tvTarget, setTvTarget] = useState(null);
  const [tvName, setTvName] = useState("");
  const claimWithTv = async () => {
    if (tvName.trim().length < 2) {
      toast.error("Please enter your TradingView Username");
      return;
    }
    setUnlocking(tvTarget.id);
    try {
      await api.post(`/member/activity/${tvTarget.id}/purchase`, { tradingview_username: tvName.trim() });
      toast.success(tvTarget.access_type === "coin" ? "Unlocked — access granted" : "Claimed — access granted");
      setTvTarget(null);
      setTvName("");
      reloadGated();
      window.dispatchEvent(new Event("pm-coin-balance-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally {
      setUnlocking("");
    }
  };

  const [booking, setBooking] = useState("");

  const loaded = signals && live && videos && ebooks && webinars && coaching && resInd && resEa;
  const ibName = signals?.ib_name || live?.ib_name || videos?.ib_name || ebooks?.ib_name || webinars?.ib_name || coaching?.ib_name || null;
  const resData = resTab === "indicator" ? resInd : resEa;
  const VALIDITY_LABEL = { permanent: "PERMANENT", weekly: "WEEKLY · 7 DAYS", monthly: "MONTHLY · 30 DAYS" };
  const MODULE_TITLES = { live: "01 · LIVE", signals: "02 · SIGNALS", e_video: "03 · E-VIDEO", e_book: "04 · E-BOOK", webinar: "05 · WEBINAR", resources: "06 · RESOURCES", coaching: "07 · PERSONAL COACHING" };
  const filterItems = (items) => (items || []).filter((i) => !query || (i.title || "").toLowerCase().includes(query.toLowerCase()));
  const pickItem = (mod, items) => items.find((i) => i.id === sel[mod]) || items[0];
  const reloadSignals = () => api.get("/member/activity/signals").then((r) => setSignals(r.data)).catch(() => {});
  useEffect(() => {
    // Pause the 20s signals poll while hidden; resume with one refresh on visibility.
    let timer = null;
    const start = () => { if (!timer) timer = setInterval(reloadSignals, 20000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else { reloadSignals(); start(); } };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const session = (live?.sessions || [])[0] || null;

  if (!loaded) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="member-live-learn-loading">
        <div className="card-lux h-28" />
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-lux h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="member-live-learn-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="eyebrow-label mb-2">Live & Learn</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white">
            Exclusive content from <span className="text-gold-gradient" data-testid="ll-ib-name">{ibName || "your IB"}</span>
          </h1>
          <p className="text-base text-[#94A3B8] mt-3 max-w-3xl leading-relaxed" data-testid="ll-disclaimer">
            Everything you see in this section is content published by {ibName || "your IB"}. PlusMaker does not create, verify, endorse, or take responsibility for the content provided here. This feature is provided as a communication and educational space for PlusMaker partners. Please use it responsibly.
          </p>
        </div>
        <p className="font-heading italic text-lg text-[#64748B] hidden md:block">Learn. Trade. Grow Together.</p>
      </div>

      {!ibName ? (
        /* No Introducing Broker assigned — elegant empty state, no fake data */
        <div className="card-lux p-12 text-center space-y-4" data-testid="ll-no-ib-state">
          <div className="mx-auto h-16 w-16 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 flex items-center justify-center">
            <Users className="h-7 w-7 text-[#D4AF37]" />
          </div>
          <h2 className="font-heading text-xl font-semibold text-white">No Introducing Broker assigned yet</h2>
          <p className="text-sm text-[#94A3B8] max-w-md mx-auto leading-relaxed">
            Once an Introducing Broker is assigned to your account, their live sessions, signals and learning content will automatically appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="card-lux p-6 sm:p-8 space-y-6">

            {/* Module launcher — entry points only; content lives INSIDE each module */}
            {!activeModule && (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="ll-launcher-grid">
              {[
                { key: "live", num: "01", name: "LIVE", desc: "Real-time sessions with your IB.", icon: Radio, accent: "text-red-400 border-red-400/25 bg-red-400/10", badge: session ? "LIVE NOW" : null },
                { key: "e_video", num: "03", name: "E-VIDEO", desc: "Educational videos from your IB.", icon: Play, accent: "text-violet-400 border-violet-400/25 bg-violet-400/10", badge: (videos.items || []).length ? `${(videos.items || []).length} AVAILABLE` : null },
                { key: "e_book", num: "04", name: "E-BOOK", desc: "PDF guides and playbooks.", icon: BookOpen, accent: "text-sky-400 border-sky-400/25 bg-sky-400/10", badge: (ebooks.items || []).length ? `${(ebooks.items || []).length} AVAILABLE` : null },
                { key: "webinar", num: "05", name: "WEBINAR", desc: "Book free or paid webinar seats.", icon: CalendarDays, accent: "text-amber-400 border-amber-400/25 bg-amber-400/10", badge: (webinars.items || []).length ? `${(webinars.items || []).length} AVAILABLE` : null },
                { key: "resources", num: "06", name: "RESOURCES", desc: "Indicators and Expert Advisors.", icon: FolderOpen, accent: "text-pink-400 border-pink-400/25 bg-pink-400/10", badge: null },
                { key: "coaching", num: "07", name: "PERSONAL COACHING", desc: "1-on-1 coaching sessions.", icon: GraduationCap, accent: "text-cyan-400 border-cyan-400/25 bg-cyan-400/10", badge: (coaching.items || []).length ? `${(coaching.items || []).length} AVAILABLE` : null },
              ].map((m) => (
                <div key={m.key} onClick={() => { setActiveModule(m.key); setQuery(""); }}
                  className="relative rounded-2xl border border-white/5 bg-white/[0.02] hover:border-[#D4AF37]/40 p-6 flex flex-col items-center text-center gap-3 cursor-pointer transition-colors"
                  data-testid={`ll-launcher-${m.key}`}>
                  {m.dot && <span data-testid="ll-signals-unread-dot" className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#0B0B0F]" />}
                  <div className={`h-12 w-12 rounded-xl border flex items-center justify-center ${m.accent}`}>
                    <m.icon className="h-5 w-5" />
                  </div>
                  <p className="font-heading text-base font-semibold text-white tracking-wide">{m.name}</p>
                  <p className="text-xs text-[#64748B]">{m.desc}</p>
                  {m.badge && <span className="text-[9px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-2 py-0.5">{m.badge}</span>}
                  <span className="mt-auto text-xs text-[#D4AF37] flex items-center gap-1.5" data-testid={`ll-open-${m.key}`}>Open <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              ))}
            </div>
            )}

            {/* Module workspace header — the user always knows which module they are inside */}
            {activeModule && (
            <div className="flex items-center gap-4 flex-wrap" data-testid="ll-module-header">
              <button onClick={() => setActiveModule(null)} className="btn-outline-gold px-4 py-2 text-xs rounded-full" data-testid="ll-module-back">← Live &amp; Learn</button>
              <p className="eyebrow-label" data-testid="ll-module-title">{MODULE_TITLES[activeModule]}</p>
            </div>
            )}

            {activeModule && (
            <div className="space-y-5" data-testid="ll-grid">
              {/* 01 — LIVE */}
              {activeModule === "live" && (
              <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 flex flex-col" data-testid="ll-live-card">
                <CardHead icon={Radio} label="LIVE" accent={{ text: "text-red-400", border: "border-red-400/25", bg: "bg-red-400/10" }}
                  badge={session ? (
                    <span className="flex items-center gap-1.5 text-[9px] font-mono tracking-widest uppercase text-red-400 border border-red-400/40 bg-red-400/10 rounded-full px-2.5 py-1" data-testid="ll-live-now-badge">
                      <span className="pm-live-dot h-1.5 w-1.5 rounded-full bg-red-400" />LIVE NOW
                    </span>
                  ) : null} />
                {session ? (
                  <>
                    <h3 className="font-heading text-lg font-semibold text-white">{session.title}</h3>
                    <p className="text-xs text-[#64748B] mt-1">with {ibName}</p>
                    <div className="flex items-center gap-2 text-xs text-[#94A3B8] mt-3">
                      <Clock className="h-3.5 w-3.5 text-[#64748B]" />
                      Started {relTime(session.starts_at || session.published_at || session.created_at)}
                    </div>
                    <button onClick={() => window.open(session.live_url, "_blank", "noopener,noreferrer")}
                      className="btn-gold w-full mt-6 px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2"
                      data-testid={`ll-join-live-${session.id}`}>
                      Join LIVE <ArrowRight className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-testid="ll-live-empty">
                    <Radio className="h-8 w-8 text-[#334155] mb-3" />
                    <p className="text-sm text-[#64748B]">No live session right now.</p>
                    <p className="text-xs text-[#475569] mt-1">When {ibName} goes live, it will appear here.</p>
                  </div>
                )}
              </div>

              )}

              {/* 03 — E-VIDEO */}
              {activeModule === "e_video" && (() => {
                const all = videos.items || [];
                if (!all.length) return <EmptyLibrary icon={Play} text="No videos yet." testid="ll-evideo-empty" />;
                const items = filterItems(all);
                const v = pickItem("e_video", items);
                return (
                  <LibraryShell icon={Play} title="E-VIDEO" tagline="Learn and grow with exclusive e-videos from your IB."
                    searchPH="Search videos..." query={query} setQuery={setQuery} testid="ll-evideo-card"
                    list={items.length ? items.map((x) => (
                      <ListRow key={x.id} img={x.media_url} icon={Play} title={x.title} desc={x.content}
                        active={!!v && v.id === x.id} onClick={() => setSel((s) => ({ ...s, e_video: x.id }))}
                        testid={`ll-evideo-item-${x.id}`} badge={<PricePill item={x} />} />
                    )) : <p className="text-xs text-[#64748B] text-center py-4">No videos match your search.</p>}
                    detail={v ? (
                      <div data-testid="ll-evideo-detail">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <ProductImg url={v.media_url} alt={v.title} icon={Play} className="w-full sm:w-60 aspect-video sm:aspect-[4/5] rounded-xl shrink-0" />
                          <div className="min-w-0 flex-1 space-y-4">
                            <PricePill item={v} testid={v.access_type === "coin" && !v.entitled ? `ll-price-${v.id}` : `ll-access-${v.id}`} />
                            <h3 className="font-heading text-2xl font-semibold text-white">{v.title}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <MetaItem icon={Users} label="Author / IB" value={ibName} />
                              <MetaItem icon={Clock} label="Published" value={fmtDT(v.published_at)} />
                            </div>
                            {v.entitled ? (
                              <a href={v.external_url} target="_blank" rel="noopener noreferrer"
                                className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2"
                                data-testid={`ll-watch-${v.id}`}>
                                Watch Now <ArrowRight className="h-4 w-4" />
                              </a>
                            ) : (
                              <button onClick={() => unlock(v)} disabled={unlocking === v.id}
                                className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid={`ll-unlock-${v.id}`}>
                                <Lock className="h-4 w-4" />{unlocking === v.id ? "Unlocking…" : `Unlock for ${fmtCoins(v.coin_price_minor)}`}
                              </button>
                            )}
                          </div>
                        </div>
                        <DescriptionBlock content={v.content} />
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-12">Select a video from the list.</p>} />
                );
              })()}

              {/* 04 — E-BOOK */}
              {activeModule === "e_book" && (() => {
                const all = ebooks.items || [];
                if (!all.length) return <EmptyLibrary icon={BookOpen} text="No e-books yet." testid="ll-ebook-empty" />;
                const items = filterItems(all);
                const b = pickItem("e_book", items);
                return (
                  <LibraryShell icon={BookOpen} title="E-BOOK" tagline="Learn and grow with exclusive e-books from your IB."
                    searchPH="Search e-books..." query={query} setQuery={setQuery} testid="ll-ebook-card"
                    list={items.length ? items.map((x) => (
                      <ListRow key={x.id} img={x.media_url} icon={BookOpen} title={x.title} desc={x.content}
                        active={!!b && b.id === x.id} onClick={() => setSel((s) => ({ ...s, e_book: x.id }))}
                        testid={`ll-ebook-item-${x.id}`} badge={<PricePill item={x} />} />
                    )) : <p className="text-xs text-[#64748B] text-center py-4">No e-books match your search.</p>}
                    detail={b ? (
                      <div data-testid="ll-ebook-detail">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <ProductImg url={b.media_url} alt={b.title} icon={BookOpen} className="w-full sm:w-60 aspect-[4/5] rounded-xl shrink-0" />
                          <div className="min-w-0 flex-1 space-y-4">
                            <PricePill item={b} testid={b.access_type === "coin" && !b.entitled ? `ll-price-${b.id}` : `ll-access-${b.id}`} />
                            <h3 className="font-heading text-2xl font-semibold text-white">{b.title}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <MetaItem icon={Users} label="Author / IB" value={ibName} />
                              <MetaItem icon={BookOpen} label="File Type" value={b.file_name || "PDF"} />
                              <MetaItem icon={Clock} label="Published" value={fmtDT(b.published_at)} />
                              <MetaItem icon={ShieldCheck} label="Access" value={b.access_type === "coin" ? fmtCoins(b.coin_price_minor) : "FREE"} />
                            </div>
                            {b.entitled ? (
                              <button onClick={() => window.open(`${api.defaults.baseURL}/member/activity/ebooks/${b.id}/download`, "_blank", "noopener,noreferrer")}
                                className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2"
                                data-testid={`ll-download-${b.id}`}>
                                <Download className="h-4 w-4" />Download PDF
                              </button>
                            ) : (
                              <button onClick={() => unlock(b)} disabled={unlocking === b.id}
                                className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid={`ll-unlock-${b.id}`}>
                                <Lock className="h-4 w-4" />{unlocking === b.id ? "Unlocking…" : `Buy for ${fmtCoins(b.coin_price_minor)}`}
                              </button>
                            )}
                          </div>
                        </div>
                        <DescriptionBlock content={b.content} />
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-12">Select an e-book from the list.</p>} />
                );
              })()}

              {/* 05 — WEBINAR */}
              {activeModule === "webinar" && (() => {
                const all = webinars.items || [];
                if (!all.length) return <EmptyLibrary icon={CalendarDays} text="No webinars scheduled yet." testid="ll-webinar-empty" />;
                const items = filterItems(all);
                const w = pickItem("webinar", items);
                return (
                  <LibraryShell icon={CalendarDays} title="WEBINAR" tagline="Book free or paid webinar seats hosted by your IB."
                    searchPH="Search webinars..." query={query} setQuery={setQuery} testid="ll-webinar-card"
                    list={items.length ? items.map((x) => (
                      <ListRow key={x.id} img={x.media_url} icon={CalendarDays} title={x.title}
                        desc={[x.event_date, x.event_time].filter(Boolean).join(" · ") || "Date TBA"}
                        active={!!w && w.id === x.id} onClick={() => setSel((s) => ({ ...s, webinar: x.id }))}
                        testid={`ll-webinar-item-${x.id}`}
                        badge={x.my_booking ? (
                          <span className={`text-[10px] font-mono tracking-wider uppercase rounded-full px-2.5 py-0.5 border ${x.my_booking.status === "approved" ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" : x.my_booking.status === "rejected" ? "text-red-400 border-red-400/40 bg-red-400/10" : "text-amber-400 border-amber-400/40 bg-amber-400/10"}`}>{x.my_booking.status}</span>
                        ) : x.access_type === "coin" ? (
                          <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5">{fmtCoins(x.coin_price_minor)}</span>
                        ) : (
                          <span className="text-[10px] font-mono tracking-wider uppercase text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 rounded-full px-2.5 py-0.5">FREE</span>
                        )} />
                    )) : <p className="text-xs text-[#64748B] text-center py-4">No webinars match your search.</p>}
                    detail={w ? (
                      <div data-testid="ll-webinar-detail">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <ProductImg url={w.media_url} alt={w.title} icon={CalendarDays} className="w-full sm:w-60 aspect-video sm:aspect-[4/5] rounded-xl shrink-0" />
                          <div className="min-w-0 flex-1 space-y-4">
                            {w.access_type === "coin" ? (
                              <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5" data-testid={`ll-webinar-price-${w.id}`}>{fmtCoins(w.coin_price_minor)}</span>
                            ) : (
                              <span className="text-[10px] font-mono tracking-wider uppercase text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 rounded-full px-2.5 py-0.5" data-testid={`ll-webinar-free-${w.id}`}>FREE</span>
                            )}
                            <h3 className="font-heading text-2xl font-semibold text-white">{w.title}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <MetaItem icon={CalendarDays} label="Date" value={w.event_date || "TBA"} />
                              <MetaItem icon={Clock} label="Time" value={w.event_time || "TBA"} />
                              <MetaItem icon={Users} label="Capacity" value={`${w.slots_left} of ${w.capacity} slots left`} testid={`ll-webinar-slots-${w.id}`} />
                              <MetaItem icon={GraduationCap} label="Host / IB" value={ibName} />
                            </div>
                            {w.my_booking?.status === "pending" ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-amber-400 border border-amber-400/30 bg-amber-400/10 rounded-full px-3 py-1.5" data-testid={`ll-webinar-status-${w.id}`}>
                                <Clock className="h-3 w-3" />Pending Approval
                              </span>
                            ) : w.my_booking?.status === "approved" ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-3 py-1.5" data-testid={`ll-webinar-status-${w.id}`}>
                                <ShieldCheck className="h-3 w-3" />Seat Booked
                              </span>
                            ) : w.slots_left === 0 ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-[#64748B] border border-white/10 bg-white/[0.03] rounded-full px-3 py-1.5" data-testid={`ll-webinar-full-${w.id}`}>Fully Booked</span>
                            ) : (
                              <div className="space-y-2">
                                <button onClick={() => bookItem(w, "webinars")} disabled={booking === w.id}
                                  className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                                  data-testid={`ll-webinar-book-${w.id}`}>
                                  <CalendarDays className="h-4 w-4" />{booking === w.id ? "Booking…" : w.access_type === "coin" ? `Book — ${fmtCoins(w.coin_price_minor)}` : "Book Free Seat"}
                                </button>
                                {w.access_type === "coin" && w.no_refund && (
                                  <p className="text-[10px] text-[#64748B] leading-snug" data-testid={`ll-webinar-norefund-${w.id}`}>{w.no_refund}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <DescriptionBlock content={w.content} />
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-12">Select a webinar from the list.</p>} />
                );
              })()}

              {/* 06 — RESOURCES */}
              {activeModule === "resources" && (() => {
                const all = resData?.items || [];
                const isInd = resTab === "indicator";
                const tabsNode = (
                  <div className="flex gap-1 rounded-full border border-white/10 p-0.5" data-testid="ll-resources-tabs">
                    {[["indicator", "Indicators"], ["ea", "Expert Advisors"]].map(([k, lbl]) => (
                      <button key={k} onClick={() => setResTab(k)}
                        className={`flex-1 text-[9px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1.5 transition-colors ${resTab === k ? "bg-pink-400/15 text-pink-300" : "text-[#64748B] hover:text-[#94A3B8]"}`}
                        data-testid={`ll-resources-tab-${k}`}>{lbl}</button>
                    ))}
                  </div>
                );
                if (!all.length) return (
                  <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-5" data-testid="ll-resources-card">
                    {tabsNode}
                    <div className="flex flex-col items-center text-center py-10" data-testid="ll-resources-empty">
                      <FolderOpen className="h-8 w-8 text-[#334155] mb-3" />
                      <p className="text-sm text-[#64748B]">No {isInd ? "indicators" : "expert advisors"} shared yet.</p>
                    </div>
                  </div>
                );
                const items = filterItems(all);
                const r = pickItem(`res_${resTab}`, items);
                return (
                  <LibraryShell icon={FolderOpen} title={isInd ? "INDICATORS" : "EXPERT ADVISORS"} tagline="Trading tools shared by your IB."
                    tabs={tabsNode} searchPH={isInd ? "Search indicators..." : "Search EAs..."} query={query} setQuery={setQuery} testid="ll-resources-card"
                    list={items.length ? items.map((x) => (
                      <ListRow key={x.id} img={x.media_url} icon={FolderOpen} title={x.title}
                        desc={x.requirements ? `Requires: ${x.requirements}` : x.content}
                        active={!!r && r.id === x.id} onClick={() => setSel((s) => ({ ...s, [`res_${resTab}`]: x.id }))}
                        testid={`ll-resource-item-${x.id}`}
                        badge={<span className="text-[10px] font-mono tracking-wider uppercase text-pink-300 border border-pink-400/30 bg-pink-400/10 rounded-full px-2.5 py-0.5" data-testid={`ll-resource-validity-${x.id}`}>{VALIDITY_LABEL[x.validity] || "PERMANENT"}</span>} />
                    )) : <p className="text-xs text-[#64748B] text-center py-4">No items match your search.</p>}
                    detail={r ? (
                      <div data-testid="ll-resource-detail">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <ProductImg url={r.media_url} alt={r.title} icon={FolderOpen} className="w-full sm:w-60 aspect-video sm:aspect-[4/5] rounded-xl shrink-0" />
                          <div className="min-w-0 flex-1 space-y-4">
                            {r.access_type === "coin" && !r.entitled ? (
                              <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5" data-testid={`ll-resource-price-${r.id}`}>{fmtCoins(r.coin_price_minor)}</span>
                            ) : (
                              <span className="text-[10px] font-mono tracking-wider uppercase text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 rounded-full px-2.5 py-0.5" data-testid={`ll-resource-access-${r.id}`}>{r.purchased ? "OWNED" : "FREE"}</span>
                            )}
                            <h3 className="font-heading text-2xl font-semibold text-white">{r.title}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <MetaItem icon={Clock} label="Validity" value={VALIDITY_LABEL[r.validity] || "PERMANENT"} />
                              <MetaItem icon={Users} label="Provider / IB" value={ibName} />
                              {r.requirements && <MetaItem icon={ShieldCheck} label="Requirements" value={r.requirements} />}
                              {r.purchased && r.access_expires_at && <MetaItem icon={CalendarDays} label="Access Until" value={fmtDT(r.access_expires_at)} testid={`ll-resource-expiry-${r.id}`} />}
                            </div>
                            {r.entitled ? (
                              <div className="space-y-2.5">
                                {r.tutorial_url && (
                                  <a href={r.tutorial_url} target="_blank" rel="noopener noreferrer"
                                    className="btn-outline-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2"
                                    data-testid={`ll-resource-tutorial-${r.id}`}>
                                    Watch Tutorial <ArrowRight className="h-4 w-4" />
                                  </a>
                                )}
                                {r.type === "resource_ea" && (
                                  <button onClick={() => window.open(`${api.defaults.baseURL}/member/activity/resource-ea/${r.id}/download`, "_blank", "noopener,noreferrer")}
                                    className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2"
                                    data-testid={`ll-resource-download-${r.id}`}>
                                    <Download className="h-4 w-4" />Download{r.file_name ? ` ${r.file_name}` : " EA"}
                                  </button>
                                )}
                                {!r.tutorial_url && r.type !== "resource_ea" && (
                                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-3 py-1.5">
                                    <ShieldCheck className="h-3 w-3" />Access Granted
                                  </span>
                                )}
                              </div>
                            ) : (
                              <button onClick={() => r.type === "resource_indicator" ? (setTvTarget(r), setTvName("")) : unlock(r)} disabled={unlocking === r.id}
                                className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid={`ll-resource-unlock-${r.id}`}>
                                <Lock className="h-4 w-4" />{unlocking === r.id ? "Processing…" : r.type === "resource_indicator" ? (r.access_type === "coin" ? `Buy for ${fmtCoins(r.coin_price_minor)}` : "Claim Free Indicator") : `Unlock for ${fmtCoins(r.coin_price_minor)}`}
                              </button>
                            )}
                          </div>
                        </div>
                        <DescriptionBlock content={r.content} />
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-12">Select an item from the list.</p>} />
                );
              })()}

              {/* 07 — PERSONAL COACHING */}
              {activeModule === "coaching" && (() => {
                const all = coaching.items || [];
                if (!all.length) return <EmptyLibrary icon={GraduationCap} text="No coaching sessions available yet." testid="ll-coaching-empty" />;
                const items = filterItems(all);
                const c = pickItem("coaching", items);
                return (
                  <LibraryShell icon={GraduationCap} title="PERSONAL COACHING" tagline="1-on-1 coaching sessions with your IB."
                    searchPH="Search coaching..." query={query} setQuery={setQuery} testid="ll-coaching-card"
                    list={items.length ? items.map((x) => (
                      <ListRow key={x.id} img={x.media_url} icon={GraduationCap} title={x.title}
                        desc={[x.location, x.availability].filter(Boolean).join(" · ") || x.content}
                        active={!!c && c.id === x.id} onClick={() => setSel((s) => ({ ...s, coaching: x.id }))}
                        testid={`ll-coaching-item-${x.id}`}
                        badge={x.my_booking?.status === "approved" ? (
                          <span className="text-[10px] font-mono tracking-wider uppercase text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 rounded-full px-2.5 py-0.5">BOOKED</span>
                        ) : (
                          <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5">{fmtCoins(x.coin_price_minor)}</span>
                        )} />
                    )) : <p className="text-xs text-[#64748B] text-center py-4">No coaching matches your search.</p>}
                    detail={c ? (
                      <div data-testid="ll-coaching-detail">
                        <div className="flex flex-col sm:flex-row gap-6">
                          <ProductImg url={c.media_url} alt={c.title} icon={GraduationCap} className="w-full sm:w-60 aspect-video sm:aspect-[4/5] rounded-xl shrink-0" />
                          <div className="min-w-0 flex-1 space-y-4">
                            <span className="text-[10px] font-mono tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-2.5 py-0.5" data-testid={`ll-coaching-price-${c.id}`}>{fmtCoins(c.coin_price_minor)}</span>
                            <h3 className="font-heading text-2xl font-semibold text-white">{c.title}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <MetaItem icon={MapPin} label="Location" value={c.location || "Online"} />
                              <MetaItem icon={Clock} label="Availability" value={c.availability || "Flexible"} />
                              <MetaItem icon={Users} label="Capacity" value={`${c.slots_left} of ${c.capacity} slots left`} testid={`ll-coaching-slots-${c.id}`} />
                              <MetaItem icon={GraduationCap} label="Coach / IB" value={ibName} />
                            </div>
                            {c.my_booking?.status === "approved" ? (
                              <div className="space-y-3">
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-3 py-1.5" data-testid={`ll-coaching-status-${c.id}`}>
                                  <ShieldCheck className="h-3 w-3" />Booked
                                </span>
                                <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/5 px-4 py-3" data-testid={`ll-coaching-contact-${c.id}`}>
                                  <p className="text-[9px] font-mono tracking-widest uppercase text-emerald-400 mb-0.5">Contact Details</p>
                                  <p className="text-sm text-[#E2E8F0] flex items-start gap-1.5">
                                    <Phone className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
                                    <span>Your phone number{c.my_booking?.client_phone ? ` (${c.my_booking.client_phone})` : ""} has been shared with your IB so they can contact you regarding your coaching session.</span>
                                  </p>
                                </div>
                              </div>
                            ) : c.slots_left === 0 ? (
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-[#64748B] border border-white/10 bg-white/[0.03] rounded-full px-3 py-1.5" data-testid={`ll-coaching-full-${c.id}`}>Fully Booked</span>
                            ) : (
                              <div className="space-y-2">
                                <button onClick={() => { setPhoneTarget(c); setPhoneValue(""); }} disabled={booking === c.id}
                                  className="btn-gold w-full px-5 py-3 text-sm rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                                  data-testid={`ll-coaching-book-${c.id}`}>
                                  <GraduationCap className="h-4 w-4" />{booking === c.id ? "Booking…" : `Book — ${fmtCoins(c.coin_price_minor)}`}
                                </button>
                                {c.no_refund && (
                                  <p className="text-[10px] text-[#64748B] leading-snug" data-testid={`ll-coaching-norefund-${c.id}`}>{c.no_refund}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <DescriptionBlock content={c.content} />
                      </div>
                    ) : <p className="text-sm text-[#64748B] text-center py-12">Select a coaching session from the list.</p>} />
                );
              })()}
            </div>
            )}
          </div>

          {/* Quote strip — dynamic IB attribution */}
          <div className="card-lux p-8 text-center" data-testid="ll-quote">
            <p className="font-heading text-xl sm:text-2xl text-white font-medium">
              <span className="text-[#D4AF37] mr-2">&ldquo;</span>Better Traders, Brighter Futures.<span className="text-[#D4AF37] ml-2">&rdquo;</span>
            </p>
            <p className="text-sm text-[#64748B] mt-3 font-heading italic">— {ibName}</p>
          </div>
        </>
      )}

      {/* Feature badges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ll-feature-badges">
        {[
          { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/25", title: "Secure & Exclusive", sub: "Only for eligible members" },
          { icon: Users, color: "text-violet-400", bg: "bg-violet-400/10 border-violet-400/25", title: "From Your IB", sub: "Personalised for your growth" },
          { icon: Zap, color: "text-sky-400", bg: "bg-sky-400/10 border-sky-400/25", title: "Real-time Updates", sub: "Get notified instantly" },
          { icon: Heart, color: "text-pink-400", bg: "bg-pink-400/10 border-pink-400/25", title: "Learn & Grow", sub: "Together with your IB" },
        ].map((f) => (
          <div key={f.title} className="card-lux p-5 flex flex-col items-center text-center gap-3" data-testid={`ll-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className={`h-11 w-11 rounded-xl border flex items-center justify-center ${f.bg}`}>
              <f.icon className={`h-5 w-5 ${f.color}`} />
            </div>
            <p className={`text-sm font-semibold ${f.color}`}>{f.title}</p>
            <p className="text-xs text-[#64748B]">{f.sub}</p>
          </div>
        ))}
      </div>

      {/* Personal Coaching — client phone required so the owning IB can contact them */}
      {phoneTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPhoneTarget(null)}>
          <div className="card-lux w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="ll-phone-modal">
            <p className="eyebrow-label mb-1">PERSONAL COACHING</p>
            <p className="text-sm text-[#94A3B8] mb-5">
              {phoneTarget.title} · <span className="text-[#D4AF37] font-medium">{fmtCoins(phoneTarget.coin_price_minor)}</span>
            </p>
            <label className="block text-xs text-[#64748B] mb-2">Phone Number <span className="text-red-400">*</span></label>
            <input value={phoneValue} onChange={(e) => setPhoneValue(e.target.value)} placeholder="+60 12-345 6789"
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-[#475569] focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="ll-phone-input" />
            <p className="text-[10px] text-[#64748B] mt-2 leading-snug">Enter your phone number so your IB can contact you regarding your coaching session.</p>
            {phoneTarget.no_refund && (
              <p className="text-[10px] text-[#64748B] mt-2 leading-snug" data-testid="ll-phone-norefund">{phoneTarget.no_refund}</p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setPhoneTarget(null)} className="btn-outline-gold px-5 py-2.5 text-sm rounded-full" data-testid="ll-phone-cancel">Cancel</button>
              <button onClick={confirmCoaching} disabled={booking === phoneTarget.id}
                className="btn-gold px-5 py-2.5 text-sm rounded-full disabled:opacity-60" data-testid="ll-phone-confirm">
                {booking === phoneTarget.id ? "Booking…" : "Confirm Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TradingView Username gate — Indicator resources only */}
      {tvTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setTvTarget(null)}>
          <div className="card-lux w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="ll-tv-modal">
            <p className="eyebrow-label mb-1">INDICATOR ACCESS</p>
            <p className="text-sm text-[#94A3B8] mb-5">
              {tvTarget.title} · <span className="text-[#D4AF37] font-medium">{tvTarget.access_type === "coin" ? fmtCoins(tvTarget.coin_price_minor) : "FREE"}</span>
            </p>
            <label className="block text-xs text-[#64748B] mb-2">TradingView Username <span className="text-red-400">*</span></label>
            <input value={tvName} onChange={(e) => setTvName(e.target.value)} placeholder="Your TradingView username"
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-[#475569] focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="ll-tv-username-input" />
            <p className="text-[10px] text-[#64748B] mt-2 leading-snug">Required so your IB can grant the indicator to the correct TradingView account.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setTvTarget(null)} className="btn-outline-gold px-5 py-2.5 text-sm rounded-full" data-testid="ll-tv-cancel">Cancel</button>
              <button onClick={claimWithTv} disabled={unlocking === tvTarget.id}
                className="btn-gold px-5 py-2.5 text-sm rounded-full disabled:opacity-60" data-testid="ll-tv-confirm">
                {unlocking === tvTarget.id ? "Processing…" : tvTarget.access_type === "coin" ? "Confirm Purchase" : "Confirm Claim"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
